import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Mail, MailOpen, Trash2, MessageCircle } from "lucide-react";

const AdminMessages = () => {
  const { authClient } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  const load = () => {
    setLoading(true);
    authClient()
      .get(`/api/admin/messages`)
      .then((r) => setMessages(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, [authClient]);

  const toggleExpand = async (m) => {
    setExpanded(expanded === m.id ? null : m.id);
    if (!m.is_read) {
      await authClient().patch(`/api/admin/messages/${m.id}/read`);
      load();
    }
  };

  const onDelete = async (id) => {
    await authClient().delete(`/api/admin/messages/${id}`);
    setConfirmingDelete(null);
    load();
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-messages-page" className="max-w-5xl">
      <div className="mb-10">
        <p className="overline mb-2">Boîte de réception</p>
        <h1 className="font-display text-4xl text-white">Messages</h1>
      </div>

      {loading ? (
        <p className="text-white/50">Chargement...</p>
      ) : messages.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/5 p-16 text-center">
          <Mail className="text-white/30 mx-auto mb-4" />
          <p className="text-white/60">Aucun message pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((m) => (
            <div
              key={m.id}
              data-testid={`message-${m.id}`}
              className={`bg-[#0a0a0a] border ${m.is_read ? "border-white/5" : "border-[#C5A059]/30"}`}
            >
              <button
                onClick={() => toggleExpand(m)}
                data-testid={`message-toggle-${m.id}`}
                className="w-full p-5 flex items-center gap-4 text-left hover:bg-white/[0.02] transition-colors"
              >
                <span className={`${m.is_read ? "text-white/30" : "text-[#C5A059]"}`}>
                  {m.is_read ? <MailOpen size={18} /> : <Mail size={18} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <p className={`text-sm font-medium truncate ${m.is_read ? "text-white/70" : "text-white"}`}>
                      {m.name}
                    </p>
                    <span className="text-xs text-white/40">·</span>
                    <p className="text-xs text-white/40">{m.email}</p>
                  </div>
                  <p className={`text-sm truncate ${m.is_read ? "text-white/50" : "text-white"}`}>{m.subject}</p>
                </div>
                <p className="text-xs text-white/40 shrink-0">{fmtDate(m.created_at)}</p>
              </button>
              {expanded === m.id && (
                <div className="border-t border-white/5 p-6 space-y-5 bg-[#080808]" data-testid={`message-detail-${m.id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="overline text-[10px] mb-1.5">Nom</p>
                      <p className="text-white">{m.name}</p>
                    </div>
                    <div>
                      <p className="overline text-[10px] mb-1.5">Email</p>
                      <a href={`mailto:${m.email}`} className="text-[#C5A059] link-underline">{m.email}</a>
                    </div>
                    {m.phone && (
                      <div>
                        <p className="overline text-[10px] mb-1.5">Téléphone</p>
                        <a href={`https://wa.me/${m.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] link-underline">
                          {m.phone}
                        </a>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="overline text-[10px] mb-2">Message</p>
                    <p className="text-white/90 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                  </div>
                  <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                    <a
                      href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                      data-testid={`reply-${m.id}`}
                      className="btn-outline"
                    >
                      <Mail size={14} /> Répondre par email
                    </a>
                    {m.phone && (
                      <a
                        href={`https://wa.me/${m.phone.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-gold"
                      >
                        <MessageCircle size={14} /> WhatsApp
                      </a>
                    )}
                    <div className="ml-auto">
                      {confirmingDelete === m.id ? (
                        <button
                          onClick={() => onDelete(m.id)}
                          data-testid={`confirm-delete-msg-${m.id}`}
                          className="px-4 py-2 bg-red-500/20 text-red-400 text-xs font-grotesk uppercase tracking-[0.16em] hover:bg-red-500/30"
                        >
                          Confirmer suppression
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirmingDelete(m.id)}
                          data-testid={`delete-msg-${m.id}`}
                          className="text-white/50 hover:text-red-400 p-2"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
